import { accountApi, pricingApi } from "../config/apiConfig";

// ── snake_case → camelCase mapping ────────────────────────────────────────────

const mapGroup = (g) => ({
  groupId: g.group_id,
  name: g.name,
  ownerEmail: g.owner_email,
  ownerEmaid: g.owner_emaid,
  tariffId: g.tariff_id,
  tariff: g.tariff ?? null,
  stationIds: g.station_ids ?? [],
  members: g.members ?? [],
  createdAt: g.created_at,
  updatedAt: g.updated_at,
});

// ── Target Groups (account-management service) ────────────────────────────────

export const getAllGroups = async () => {
  try {
    const response = await accountApi.get("/target-groups/all");
    return (response?.data ?? []).map(mapGroup);
  } catch (error) {
    console.error("Failed to fetch groups", error);
    throw error;
  }
};

export const getGroupById = async (groupId) => {
  try {
    const response = await accountApi.get(`/target-groups/${groupId}`);
    return mapGroup(response?.data);
  } catch (error) {
    console.error("Failed to fetch group", error);
    throw error;
  }
};

/**
 * @param {{ name: string, stationIds: string[], memberIdentifiers?: Array<{email: string} | {emaid: string}>, tariff?: object | null }} group
 */
export const createGroup = async (group) => {
  try {
    const response = await accountApi.post("/target-groups", {
      name: group.name,
      station_ids: group.stationIds,
      member_identifiers: group.memberIdentifiers,
      tariff: group.tariff,
    });
    return mapGroup(response?.data);
  } catch (error) {
    console.error("Failed to create group", error);
    throw error;
  }
};

/**
 * Partial update — only the fields provided are changed.
 * Passing tariff: null explicitly clears the tariff assignment.
 * @param {string} groupId
 * @param {{ name?: string, stationIds?: string[], memberIdentifiers?: Array<{email: string} | {emaid: string}>, tariff?: object | null }} patch
 */
export const updateGroup = async (groupId, patch) => {
  try {
    const body = {};
    if (patch.name !== undefined) body.name = patch.name;
    if (patch.stationIds !== undefined) body.station_ids = patch.stationIds;
    if (patch.memberIdentifiers !== undefined) body.member_identifiers = patch.memberIdentifiers;
    if ("tariff" in patch) body.tariff = patch.tariff;
    const response = await accountApi.patch(`/target-groups/${groupId}`, body);
    return mapGroup(response?.data);
  } catch (error) {
    console.error("Failed to update group", error);
    throw error;
  }
};

export const deleteGroup = async (groupId) => {
  try {
    const response = await accountApi.delete(`/target-groups/${groupId}`);
    return response?.data;
  } catch (error) {
    console.error("Failed to delete group", error);
    throw error;
  }
};

/**
 * Returns users already placed in another group on any of the given stations.
 * Pass excludeGroupId when editing an existing group so its own members aren't flagged.
 * @param {{ stationIds: string[], excludeGroupId?: string }} params
 */
export const getExcludedUsers = async ({ stationIds, excludeGroupId }) => {
  try {
    const body = { station_ids: stationIds };
    if (excludeGroupId) body.exclude_group_id = excludeGroupId;
    const response = await accountApi.post("/target-groups/excluded-users", body);
    return response?.data;
  } catch (error) {
    console.error("Failed to fetch excluded users", error);
    throw error;
  }
};

/**
 * Returns the groups the authenticated user is a member of.
 */
export const getMemberships = async () => {
  try {
    const response = await accountApi.get("/target-groups/memberships");
    return response?.data;
  } catch (error) {
    console.error("Failed to fetch memberships", error);
    throw error;
  }
};

// ── Tariffs (pricing service) ─────────────────────────────────────────────────

/**
 * Lists tariffs for a station. Caller must own the station (owner:{locationId}) or be admin.
 * @param {string} locationId
 */
export const getTariffsByLocation = async (locationId) => {
  try {
    const response = await pricingApi.get(`/static-tariff/tariffs/${locationId}`);
    return response?.data;
  } catch (error) {
    console.error("Failed to fetch tariffs for location", error);
    throw error;
  }
};

/**
 * Hydrates a single tariff by id. No auth required.
 * @param {string} tariffId
 */
export const getTariffById = async (tariffId) => {
  try {
    const response = await pricingApi.get(`/tariffs/${tariffId}`);
    return response?.data;
  } catch (error) {
    console.error("Failed to fetch tariff", error);
    throw error;
  }
};

/**
 * Returns the tariff that would apply to userEmail on locationId,
 * taking target-group tariffs into account.
 * @param {string} locationId
 * @param {string} userEmail
 */
export const resolveUserTariff = async (locationId, userEmail) => {
  try {
    const response = await pricingApi.get(
      `/static-tariff/tariffs/${locationId}/resolve`,
      { params: { user_email: userEmail } }
    );
    return response?.data;
  } catch (error) {
    console.error("Failed to resolve user tariff", error);
    throw error;
  }
};
